import React, { useContext, useEffect, useRef, useState } from "react";
import de from "./locales/de";
import { default as Translations, default as TranslationsType } from "./types";

export type Locale = "de" | "en";

type UpdateLanguageFn = (language: Locale | "dev") => void;

type TranslationsContextProps = {
  locale: Locale;
  translations: TranslationsType;
  updateLanguage: UpdateLanguageFn;
};

function mutateDevTranslations(
  translations: TranslationsType | Record<string, unknown>,
  path = "",
) {
  if (typeof translations !== "object" || !translations) return;
  for (const [key, value] of Object.entries(translations)) {
    const keyPath = path ? `${path}.${key}` : key;
    if (typeof value === "string") {
      (translations as Record<string, unknown>)[key] = keyPath;
    } else {
      mutateDevTranslations(value as Record<string, unknown>, keyPath);
    }
  }
}

async function setDevTranslations(
  setTranslations: (translations: TranslationsType) => void,
) {
  const { default: cloneDeep } = await import("lodash/cloneDeep");
  const devTranslations = cloneDeep(de);
  mutateDevTranslations(devTranslations);
  setTranslations(devTranslations);
}

const updateLanguage = async (
  language: string,
  setTranslations: (translations: TranslationsType) => void,
  setLocale: (locale: Locale) => void,
) => {
  if (language === "dev") {
    setDevTranslations(setTranslations);
    setLocale("en");
  } else if (language === "de") {
    setTranslations(de);
    setLocale("de");
  } else {
    import("./locales/en").then((file) => {
      setTranslations(file.default);
      setLocale("en");
    });
  }
};

export const TranslationsContext =
  React.createContext<TranslationsContextProps>({
    locale: "de",
    translations: de,
    updateLanguage: () => {},
  });

export function withTranslations<Outer extends Record<string, unknown>>(
  Component: React.ComponentType<
    Outer & { locale: Locale; translations: Translations }
  >,
) {
  return (props: Outer) => {
    const { locale, translations } =
      useContext<TranslationsContextProps>(TranslationsContext);

    return <Component {...props} locale={locale} translations={translations} />;
  };
}

export function AsyncTranslationsProvider({
  children,
  useGetLanguage,
}: {
  children: React.ReactNode;
  useGetLanguage?: () => string | undefined;
}) {
  const language = useGetLanguage?.();
  const [locale, setLocale] = useState<Locale>("de");
  const [translations, setTranslations] = useState<TranslationsType>(de);

  useEffect(() => {
    if (language) {
      updateLanguage(language, setTranslations, setLocale);
    }
  }, [language]);

  return (
    <TranslationsContext.Provider
      value={{
        locale,
        translations,
        updateLanguage: (locale: Locale | "dev") =>
          updateLanguage(locale, setTranslations, setLocale),
      }}
    >
      {children}
    </TranslationsContext.Provider>
  );
}

export function useLocale(): Locale {
  return useContext(TranslationsContext).locale;
}

export function useTranslations(): TranslationsType {
  return useContext(TranslationsContext).translations;
}

export function evaluateTranslation(
  translation: string | ((...args: unknown[]) => string),
  ...rest: unknown[]
) {
  return typeof translation === "function" ? translation(...rest) : translation;
}

/**
 * Collects the dotted paths of every leaf error inside a react-hook-form
 * `formState.errors` tree (e.g. `patient.profile.first_name`,
 * `guardians.0.name`). An RHF leaf error always carries `type`/`message`/`ref`,
 * which lets us distinguish it from intermediate nested objects.
 */
function collectErrorPaths(errors: unknown, prefix = ""): string[] {
  if (errors == null || typeof errors !== "object") return [];
  const node = errors as Record<string, unknown>;
  if (typeof node.message === "string" || typeof node.type === "string") {
    return prefix ? [prefix] : [];
  }
  if (Array.isArray(errors)) {
    return errors.flatMap((item, idx) =>
      collectErrorPaths(item, prefix ? `${prefix}.${idx}` : String(idx)),
    );
  }
  return Object.entries(node).flatMap(([key, value]) =>
    collectErrorPaths(value, prefix ? `${prefix}.${key}` : key),
  );
}

/**
 * Refreshes already-visible Zod error messages when the active translations
 * change. Zod resolves messages at validation time and react-hook-form caches
 * the resulting strings in `formState.errors`, so a language switch leaves
 * those strings in the previous locale until the field is re-validated.
 *
 * Rather than re-running the whole schema (which would be heavy on large
 * forms and could surface errors the user hasn't seen yet), we collect the
 * paths of fields that already have errors and ask RHF to re-validate only
 * those. Forms without visible errors do no work at all.
 */
export function useRevalidateOnTranslationsChange<
  M extends {
    trigger: (...args: never[]) => Promise<boolean>;
    formState: { errors: Record<string, unknown> };
  },
>(methods: M) {
  const translations = useTranslations();
  const previousTranslationsRef = useRef(translations);

  useEffect(() => {
    if (previousTranslationsRef.current === translations) return;
    previousTranslationsRef.current = translations;

    const erroredPaths = collectErrorPaths(methods.formState.errors);
    if (erroredPaths.length > 0) {
      (methods.trigger as (paths: string[]) => Promise<boolean>)(erroredPaths);
    }
  }, [translations, methods]);
}
